import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export type RemoteSession = {
  id: string;
  device_id: string;
  session_type: 'terminal' | 'control';
  status: string;
  created_by: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
};

export const useRemoteSession = (deviceId: string) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const cleanupExistingSessions = async () => {
    console.log('Cleaning up existing sessions for device:', deviceId);
    const { error } = await supabase
      .from('remote_sessions')
      .update({ 
        status: 'ended',
        ended_at: new Date().toISOString()
      })
      .eq('device_id', deviceId)
      .eq('status', 'active');

    if (error) {
      console.error('Error cleaning up sessions:', error);
      throw error;
    }
  };

  const { data: activeSession, isLoading: isLoadingSession, refetch } = useQuery({
    queryKey: ['remote-session', deviceId],
    queryFn: async () => {
      console.log('Fetching active session for device:', deviceId);
      const { data, error } = await supabase
        .from('remote_sessions')
        .select('*')
        .eq('device_id', deviceId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        console.error('Error fetching active session:', error);
        throw error;
      }
      
      if (data) {
        console.log('Found active session:', data);
        setSessionId(data.id);
      } else {
        console.log('No active session found');
      }
      
      return data as RemoteSession | null;
    },
    enabled: !!deviceId,
    refetchInterval: (query) => {
      // Use exponential backoff: if no active session, poll less frequently
      const hasActiveSession = query.state.data;
      return hasActiveSession ? 10000 : 15000; // 10s with session, 15s without
    },
    staleTime: 8000, // Consider data fresh for 8 seconds
  });

  const startSession = useMutation({
    mutationFn: async (type: 'terminal' | 'control') => {
      console.log('Starting new session:', type);
      
      // First, cleanup any existing sessions
      await cleanupExistingSessions();
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('remote-session', {
        body: { 
          device_id: deviceId, 
          session_type: type,
          user_id: user.id
        }
      });

      if (error) {
        console.error('Error starting session:', error);
        throw error;
      }

      console.log('Session started:', data);
      return data.session;
    },
    onSuccess: (newSession) => {
      console.log('Session successfully created, updating state:', newSession);
      setSessionId(newSession.id);
      
      // Immediately update the cache with the new session
      queryClient.setQueryData(['remote-session', deviceId], newSession);
      
      // Invalidate to trigger a refetch and ensure sync
      queryClient.invalidateQueries({ queryKey: ['remote-session', deviceId] });
      
      toast.success('Terminal session started');
    },
    onError: (error) => {
      console.error('Error starting remote session:', error);
      toast.error('Failed to start remote session');
    }
  });

  const sendCommand = useMutation({
    mutationFn: async (command: string) => {
      if (!activeSession?.id) {
        console.error('No active session');
        throw new Error('No active session');
      }

      console.log('Sending command:', command, 'to session:', activeSession.id);
      const { data, error } = await supabase.functions.invoke('terminal-command', {
        body: { 
          session_id: activeSession.id, 
          command,
          device_id: deviceId
        }
      });

      if (error) {
        console.error('Error sending command:', error);
        throw error;
      }

      console.log('Command queued:', data);
      return data;
    },
    onSuccess: () => {
      console.log('Command successfully queued for execution');
    },
    onError: (error) => {
      console.error('Error sending command:', error);
      toast.error('Failed to send command');
    }
  });

  return {
    activeSession,
    isLoadingSession,
    startSession,
    sendCommand
  };
};